import styled from 'styled-components'
import { type ChangeEvent, useRef } from 'react'

import SearchCircleIcon from './assets/SearchCircleIcon'

export interface Props {
  placeholder?: string
  disabled?: boolean
  onClick?: () => void
  onChange?: (ev: ChangeEvent<HTMLInputElement>) => void
  onPressEnter?: (value: string, type: 'keyboard' | 'button') => void
}

const SearchInput = ({ onClick, onChange, onPressEnter, placeholder, disabled }: Props): JSX.Element => {
  const inputEl = useRef<HTMLInputElement>(null)

  const clearInputValue = () => {
    if (inputEl.current) {
      inputEl.current.value = ''
    }
  }

  const onClickIcon = () => {
    if (!disabled && inputEl.current && onPressEnter) {
      onPressEnter(inputEl.current.value, 'button')
      clearInputValue()
    }
  }

  return (
    <Wrapper onClick={onClick}>
      <div>
        {disabled ? (
          <StyledPlaceholderText>{placeholder}</StyledPlaceholderText>
        ) : (
          <form
            action="."
            onSubmit={(e) => {
              e.preventDefault()
              const keyword = inputEl.current?.value
              if (keyword && onPressEnter) {
                onPressEnter(keyword, 'keyboard')
                clearInputValue()
                inputEl.current?.blur()
              }
            }}
          >
            <input ref={inputEl} type="search" id="searchInput" placeholder={placeholder} onChange={onChange} />
          </form>
        )}
        <StyledIconWrapper onClick={onClickIcon}>
          <SearchCircleIcon />
        </StyledIconWrapper>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  box-sizing: border-box;
  padding: 16px 26px 0;

  & > div {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid;
    border-radius: 100px;
    padding: 7px;
    border-color: ${({ theme }) => theme.colors.orange};
    form {
      display: flex;
      align-items: center;
      flex-grow: 1;
    }

    input[type='search'] {
      ${({ theme }) => theme.typography.description1};
      border-width: 0px;
      padding: 0 0px 0 13px;
      margin: 0px;
      width: calc(100% - 20px);
      outline: none;
      background-color: ${({ theme }) => theme.colors.white};
      color: ${({ theme }) => theme.colors.black};

      &::placeholder {
        color: ${({ theme }) => theme.colors.gray800};
      }
      &::-webkit-search-cancel-button,
      &::-webkit-search-results-button,
      &::-webkit-search-decoration {
        -webkit-appearance: none;
      }
    }
  }
`

const StyledPlaceholderText = styled.div`
  margin-left: 13px;
  ${({ theme }) => theme.typography.description1}
  color: ${({ theme }) => theme.colors.gray800};
`

const StyledIconWrapper = styled.div`
  line-height: 0;
  cursor: pointer;
`

export default SearchInput
